import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdClose, MdLock, MdCheckCircle } from 'react-icons/md';
import { getUser, getAuthToken, saveAuth } from '../services/auth';
import api from '../services/api';
import { zIndexClasses } from '../utils/layering';

export default function PaywallModal({ isOpen, onClose, type = 'message', onActivated, onError }) {
  const navigate = useNavigate();
  const user = getUser();
  const [isClosing, setIsClosing] = useState(false);
  const [activating, setActivating] = useState(false);
  
  if (!isOpen) return null;
  
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };
  
  const handleAction = async () => {
    if (!user) {
      navigate('/register');
      handleClose();
      return;
    }
    // Demo: activate premium with one click (no real payment)
    setActivating(true);
    try {
      const res = await api.post('/auth/activate-demo-premium');
      const updatedUser = res?.data?.user;
      const token = getAuthToken();
      if (updatedUser && token) {
        saveAuth(token, { ...updatedUser, hasPremium: true });
      }
      if (typeof onActivated === 'function') {
        onActivated(updatedUser);
      }
      handleClose();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('user-updated'));
      }
    } catch (err) {
      console.error(err);
      if (typeof onError === 'function') onError(err);
    } finally {
      setActivating(false);
    }
  };
  
  const title = !user 
    ? 'Registrujte se da kontaktirate'
    : 'Aktivirajte pretplatu za dopisivanje';
    
  const description = !user
    ? 'Kreirajte nalog da biste mogli da šaljete poruke dadiljama ili roditeljima i započnete saradnju.'
    : 'Aktivirajte pretplatu da biste mogli da šaljete poruke i komunicirate sa drugim korisnicima.';
    
  const benefits = !user
    ? [
        'Pretraga i pregled profila',
        'Kontaktiranje dadilja/roditelja',
        'Upravljanje kalendarom',
        'Praćenje plaćanja'
      ]
    : [
        'Neograničeno slanje poruka',
        'Direktna komunikacija sa korisnicima',
        'Brže odgovore i bolju saradnju',
        'Premium podrška'
      ];
  
  return (
    <div 
      className={`fixed inset-0 ${zIndexClasses.modal} flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-transform ${
          isClosing ? 'scale-95' : 'scale-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <MdLock className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>
        
        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {description}
        </p>
        
        {/* Benefits */}
        <div className="space-y-3 mb-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-3">
              <MdCheckCircle className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {benefit}
              </span>
            </div>
          ))}
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Kasnije
          </button>
          <button
            onClick={handleAction}
            disabled={activating}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-70"
          >
            {!user ? 'Registruj se' : activating ? 'Aktiviranje…' : 'Aktiviraj pretplatu'}
          </button>
        </div>
      </div>
    </div>
  );
}
